import { Component, EventEmitter, HostBinding, Input, Output, ViewChild } from '@angular/core';
import { DxPopupComponent } from 'devextreme-angular';
import { HiddenEvent, InitializedEvent, ResizeEvent, ShowingEvent } from 'devextreme/ui/popup';
import { OnPopupClosePayload } from './modal-window.models';
import { ModalWindowService } from './modal-window.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs';
import { ModalWindowUserAction } from './modal-window.model';
import { SafeHtml } from '@angular/platform-browser';

/**
 * An extra control in the dialog's own toolbar, beside the title.
 *
 * Was `[]`, which infers never[] and quietly rejected every caller that tried to pass one.
 */
export type ModalWindowToolbarItem = {
  widget?: string;
  visible?: boolean;
  // Plain strings, not DevExtreme's literal unions: callers build these objects freely and
  // narrowing here rejects every existing one.
  location?: string;
  toolbar?: string;
  cssClass?: string;
  options?: Record<string, unknown>;
  // Callers also set DevExtreme item properties directly on the item, `disabled` among
  // them. This stays open so naming the common fields does not break them.
  [key: string]: unknown;
};

@UntilDestroy()
@Component({
  selector: 'ag-shr-modal-window',
  templateUrl: './modal-window.component.html',
  styleUrls: ['./modal-window.component.scss'],
})
export class ModalWindowComponent {
  @HostBinding('class') className = 'modal-window';
  @ViewChild('popupRef', { static: true }) popupComponent: DxPopupComponent;
  @Input() modalId: string;
  @Input() inProgress: boolean;
  /**
   * Data the modal needs is still loading. Distinct from `inProgress`, which is a
   * SAVE in flight and shows inside the action button.
   *
   * Shows a full-screen shaded panel, and works whether or not the popup is open —
   * so a modal whose size depends on its data can fetch FIRST and then open at its
   * final size, instead of opening empty and resizing under the user.
   */
  @Input() isLoading = false;
  @Input() isFullScreen: boolean = false;
  @Input() title: string;
  @Input() htmlTitle: SafeHtml;
  @Input() actionTitle: string = 'SAVE';
  @Input() actionIcon: string = 'save';
  @Input() cancelTitle: string = 'CANCEL';
  @Input() useScrollView = true;
  @Input() showFullScreenButton = true;
  @Input() showSaveButton = true;
  @Input() showCancelButton = true;
  @Input() showCloseButton = true;
  @Input() closeOnEscapeButton = true;
  @Input() saveButtonDisabled = false;
  @Input() shading = true;
  @Input() width: string | number = '80vw';
  @Input() height: string | number = '80vh';
  @Input() extraToolbarItems: ModalWindowToolbarItem[] = [];
  @Input() customClass = '';
  @Input() container = undefined;
  @Input() onCloseModal: () => Promise<boolean>;

  @Output() onSaveClick = new EventEmitter<{ component: DxPopupComponent }>();
  @Output() onResizeModal = new EventEmitter<ResizeEvent>();
  /**
   * @deprecated Use onCloseModal instead
   */
  @Output() onPopupClose = new EventEmitter<OnPopupClosePayload>();
  @Output() onShown = new EventEmitter<ShowingEvent>();
  @Output() onShowing = new EventEmitter<any>();
  @Output() onHidden = new EventEmitter<HiddenEvent>();

  // @HostListener('document:fullscreenchange')
  // fullscreenChanged = () => {
  //   this.popupComponent.container = document.fullscreenElement ?? document.body;
  // };
  constructor(private modalWindowService: ModalWindowService) {
    this.modalWindowService.showModal$
      .pipe(
        untilDestroyed(this),
        filter(modalId => {
          const thisModalId = this.modalId;

          return modalId && thisModalId === modalId;
        }),
      )
      .subscribe(() => {
        this.showModal();
      });
  }

  protected onInitialized = (e: InitializedEvent) => {
    e.component.instance().on('hiding', this.onClosePopup);

    if (!this.closeOnEscapeButton) {
      e.component.registerKeyHandler('escape', function (event) {
        event.stopPropagation();
      });
    }
  };

  public showModal = () => {
    this.popupComponent.instance.show();
  };

  public hideModal = () => {
    this.popupComponent.instance.hide();
  };

  protected handelSaveClick = () => {
    this.modalWindowService.userAction$.next({
      modalId: this.modalId,
      type: ModalWindowUserAction.apply,
      component: this.popupComponent,
      modalWindow: this,
    });
    this.onSaveClick.emit({ component: this.popupComponent });
  };

  public toggleScreenSize = () => {
    const popup = this.popupComponent.instance;
    const isFullScreen = !popup.option('fullScreen');

    this.isFullScreen = isFullScreen;
    popup.option('fullScreen', isFullScreen);

    setTimeout(() => {
      popup.repaint();
    }, 0);
  };

  forceCloseModal = async (/* component: dxPopup<Properties> */) => {
    const component = this.popupComponent.instance;
    component.off('hiding', this.onClosePopup);
    await this.popupComponent.instance.hide();
    component.on('hiding', this.onClosePopup);
  };

  protected handleResize = e => {
    console.log('ModalWindowComponent handleResize', e);
    this.onResizeModal.emit(e);
  };

  private onClosePopup = async event => {
    this.onPopupClose.emit({ event, component: this.popupComponent });

    if (!this.onCloseModal) {
      this.modalWindowService.userAction$.next({
        modalId: this.modalId,
        type: ModalWindowUserAction.abort,
        component: this.popupComponent,
        modalWindow: this,
      });
      return;
    }
    event.cancel = true;

    const isCloseConfirmed = await this.onCloseModal();

    if (isCloseConfirmed) {
      this.modalWindowService.userAction$.next({
        modalId: this.modalId,
        type: ModalWindowUserAction.abort,
        component: this.popupComponent,
        modalWindow: this,
      });
      await this.forceCloseModal();
    }
  };
}
